import { forwardRef, useMemo } from 'react';

import { Box, BoxProps, FormLabel, TextField, TextFieldProps } from '@mui/material';
import { useField } from 'formik';

export type OptionTypes = {
    businessId: number;
    linkedId?: number[];
    type: string;
    value: string | number;
    label: string;
    disabled?: boolean;
};

type FieldInputProps = {
    name: string;
    label?: string;
    placeholder?: string;
    boxStyling?: BoxProps;
    type?: string;
    disabled?: boolean;
    size?: 'small' | 'medium';
    alignInput?: 'center' | 'left' | 'right';
    textFieldProps?: TextFieldProps;
    dataTestId?: string;
    minVal?: number;
    maxVal?: number;
    allowDecimal?: boolean;
};

export const FieldInput = forwardRef<HTMLInputElement, FieldInputProps>(
    (
        {
            name,
            label = '',
            boxStyling,
            placeholder = '',
            type = 'text',
            disabled,
            size = 'medium',
            alignInput = 'left',
            textFieldProps,
            dataTestId = `${name}-input`,
            minVal,
            maxVal,
            allowDecimal = true,
        },
        ref
    ) => {
        const [{ value, onBlur }, { error, touched }, { setValue }] = useField(name);

        const hasError = useMemo(() => touched && error, [touched, error]);

        const borderColor = hasError ? 'red' : '#F39E4E';

        const parseNumber = (value: string) => {
            let numberValue = parseInt(value);
            if (allowDecimal) {
                numberValue = parseFloat(parseFloat(value).toFixed(2));
            }
            if (minVal && numberValue < minVal) {
                numberValue = minVal;
            }
            if (maxVal && numberValue > maxVal) {
                numberValue = maxVal;
            }
            return numberValue;
        };

        return (
            <Box display="flex" flexDirection="column" flex={0.49} {...boxStyling}>
                {label && (
                    <FormLabel sx={{ marginBottom: '8px', color: '#000', fontWeight: 600 }}>
                        {label}
                    </FormLabel>
                )}

                <TextField
                    inputProps={{
                        'data-testid': dataTestId,
                    }}
                    name={name}
                    value={value}
                    onChange={({ target: { value } }) => {
                        if (type == 'number') {
                            setValue(parseNumber(value));
                        } else {
                            setValue(value);
                        }
                    }}
                    onBlur={onBlur}
                    placeholder={placeholder ? placeholder : label ? `Enter ${label}` : ''}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor,
                            },
                            '&:hover fieldset': {
                                borderColor,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor,
                            },
                        },
                        '& input[type=number]': {
                            '-moz-appearance': 'textfield',
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                            '-webkit-appearance': 'none',
                            margin: 0,
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                            '-webkit-appearance': 'none',
                            margin: 0,
                        },
                        '& .MuiOutlinedInput-input': {
                            textAlign: alignInput,
                        },
                    }}
                    type={type}
                    autoComplete="off"
                    disabled={disabled}
                    size={size}
                    inputRef={ref}
                    {...textFieldProps}
                />
                {hasError && <label className="error">{error}</label>}
            </Box>
        );
    }
);
